import { type GetServerSideProps } from 'next'
import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import Layout from '@/components/Layout'
import type { CustomContext, ContentPageProps } from '@/types'
import DEFAULT_META_TAGS from '@/constants/meta'
import Cobrand from '@/components/Cobrand'
import Meta from '@/components/Meta'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import LandingPage from '@/components/VIP/LandingPage'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import setiService from '../server/middleware/seti'
import mapSetiResponse from '../utils/map-seti-response'
import oktaJwtVerify from '../server/utils/okta-jwt-verifier'
import getContentfulData from '../server/utils/get-contentful-data'
import retrieveContentfulSecrets from '../server/utils/retrieve-contentful-secrets'

function Vip({
  content,
  headerHTML,
  footerHTML,
  bootstrapData
}: ContentPageProps) {
  const canonicalUrl = useCanonicalUrl()
  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.vip}
        pageTitle={DEFAULT_META_TAGS.title.vip}
        canonicalUrl={canonicalUrl}
      />
      <FeatureProvider features={bootstrapData.featureToggles}>
        <ContentfulProvider contentful={content}>
          <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
            <Cobrand />
            <LandingPage />
          </Layout>
        </ContentfulProvider>
      </FeatureProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<
  ContentPageProps
> = async ctx => {
  const { req, res } = ctx as CustomContext

  const bootstrapData = generateBootstrapData('vip', req, res)
  const { webstats, referral, appVersion } = bootstrapData
  const { clientGUID: cguid } = webstats
  const { contentfulDeliverySecret, contentfulPreviewSecret } =
    retrieveContentfulSecrets()
  const isContentfulEnabled =
    res.locals.features?.siteExperience.isContentfulEnabled ?? true
  const [pclnHeaderFooterContent, signInStatus, { content, experimentNames }] =
    await Promise.all([
      fetchPCLNHeaderFooterContent(req, res),
      oktaJwtVerify({ authHdr: req.headers.authorization ?? '', cguid }),
      getContentfulData({
        contentTypeId: 'pageLanding',
        contentfulDeliverySecret,
        contentfulPreviewSecret,
        product: 'Promotion',
        contentPreview: ctx.query['content-preview'] === 'true',
        isContentfulEnabled
      })
    ])

  if (res) {
    if (signInStatus.signedIn) {
      res.writeHead(302, { Location: `/next-profile${req.originalUrl}` })
      res.end()
    }
  }
  const setiResponseEvents = await setiService(req, {
    appVersion,
    allowSetiOverrides: true,
    webstats,
    referral,
    reqQueryParams: ctx.query,
    ...(experimentNames && {
      additionalExperiments: experimentNames
    })
  })

  const bootstrapWithJwtSignInData = {
    ...bootstrapData,
    customer: {
      signedIn: signInStatus.signedIn ?? false,
      emailAddress: signInStatus.email ?? '',
      firstName: String(signInStatus.firstName ?? '')
    }
  }

  return {
    props: {
      content,
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapWithJwtSignInData,
        fullSETI: setiResponseEvents,
        seti: mapSetiResponse(setiResponseEvents),
        signInStatus
      }
    }
  }
}

export default Vip
